import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, inject, computed } from '@angular/core';
import { Router } from '@angular/router';
import {
  CartV2Query,
  CartV2Service,
  CheckoutService,
  DISABLED_STATUSES,
  ITEM_CART_TYPE,
  REWARD_STATUS,
  SUMMARY_PRICE_KIND,
} from '@fgb/core';
import gsap from 'gsap';
import { PipesModule } from '@fgb/portal-component-library/src/lib/pipes';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';

@Component({
  selector: 'fgbcl-cart-button',
  standalone: true,
  imports: [CommonModule, PipesModule, FGBSharedModule],
  templateUrl: './cart-button.component.html',
  styleUrl: './cart-button.component.scss',
})
export class CartButtonComponent implements AfterViewInit, OnDestroy {
  @ViewChild('dropdown') dropdownRef: ElementRef<HTMLDivElement>;
  @ViewChild('cartList') cartItemsRef: ElementRef<HTMLUListElement>;
  @ViewChild('cartBtn') cartBtnRef: ElementRef<HTMLButtonElement>;
  isOpen = false;
  private _openTl: gsap.core.Timeline | null = null;
  private _closeTl: gsap.core.Timeline | null = null;
  private router = inject(Router);
  private cartQuery = inject(CartV2Query);
  private cartService = inject(CartV2Service);
  private checkoutService = inject(CheckoutService);
  cartItems = this.cartQuery.items;
  cartSummary = this.cartQuery.summary;
  totalPoints = computed(() => this.cartSummary().pointsItems.total);
  totalMerchantPrice = computed(() => this.cartSummary().merchantItems.total);
  totalProductPrice = computed(() => this.cartSummary().productItems.total);
  totalEcashPrice = computed(() => this.cartSummary().ecashItems.total);
  /** Number of individual units in the basket, shown as the count badge in the dropdown header. */
  totalItems = computed(() => this.cartSummary().totalQuantity);
  /**
   * Money owed in merchant minor units. The cart has no server-calculated basket, so there are no
   * taxes to add here - this is the whole of what the member pays in money.
   */
  totalPrice = computed(() => this.totalMerchantPrice() + this.totalProductPrice() + this.totalEcashPrice());
  hasPoints = computed(() => this.totalPoints() > 0);
  hasMoneyTotal = computed(() => this.totalPrice() > 0);
  isFreeBasket = computed(() => !this.hasMoneyTotal() && !this.hasPoints());
  isDisabled = computed(() =>
    this.cartItems().some((item) => item?.productData && DISABLED_STATUSES.has(item.productData.Status))
  );
  marketplaceStatus = REWARD_STATUS;
  itemCartType = ITEM_CART_TYPE;
  summaryPriceKind = SUMMARY_PRICE_KIND;

  ngAfterViewInit(): void {
    gsap.set(this.dropdownRef.nativeElement, { autoAlpha: 0, y: -8, scale: 0.97, transformOrigin: 'top right' });
  }

  toggleCart(): void {
    this.isOpen ? this.closeCart() : this.openCart();
  }

  openCart(): void {
    if (this.isOpen) return;
    this.isOpen = true;

    if (this._closeTl) this._closeTl.kill();

    const items = this.cartItemsRef?.nativeElement.querySelectorAll('.summary-item');

    this._openTl = gsap
      .timeline()
      .to(this.dropdownRef.nativeElement, { autoAlpha: 1, y: 0, scale: 1, duration: 0.22, ease: 'power2.out' })
      .fromTo(items, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.2, stagger: 0.06, ease: 'power2.out' }, '-=0.08');

    this._animateBadge();
    this.cartService.refreshItems();
  }

  closeCart(): void {
    if (!this.isOpen) return;
    if (this._openTl) this._openTl.kill();

    this._closeTl = gsap.timeline().to(this.dropdownRef.nativeElement, {
      autoAlpha: 0,
      y: -8,
      scale: 0.97,
      duration: 0.18,
      ease: 'power2.in',
      onComplete: () => {
        this.isOpen = false;
      },
    });
  }

  /**
   * Which price the member is actually paying for a line. E-cash first, then merchant, then
   * points, matching `CheckoutService.resolvePriceKind` so the cart and the checkout summary
   * never disagree about how the same line is priced.
   */
  priceKindOf(item: any): SUMMARY_PRICE_KIND {
    const productData = item?.productData;
    if (!productData) return SUMMARY_PRICE_KIND.FREE;
    if (productData.EcashValue > 0) return SUMMARY_PRICE_KIND.ECASH;
    if (this.merchantTotalOf(item) > 0) return SUMMARY_PRICE_KIND.MERCHANT;
    if (productData.PointValue > 0) return SUMMARY_PRICE_KIND.POINTS;
    return SUMMARY_PRICE_KIND.FREE;
  }

  /**
   * Which kind of basket line this is. Derived from the product data rather than read from
   * `Cart.type`, because the createMarketplaceCartItem/createPortalProductCartItem factories
   * never populate that field - it is declared on the interface but cast away with `as Cart`.
   */
  cartTypeOf(item: any): ITEM_CART_TYPE | null {
    const productData = item?.productData;
    if (!productData) return null;
    return 'ProductId' in productData ? ITEM_CART_TYPE.PRODUCT : ITEM_CART_TYPE.MARKETPLACE;
  }

  /** Line total in merchant minor units. Portal products price on MerchantPrice, rewards on MerchantValue. */
  merchantTotalOf(item: any): number {
    const productData = item?.productData;
    if (!productData) return 0;
    const unitPrice = 'ProductId' in productData ? productData.MerchantPrice : productData.MerchantValue;
    return item.purchaseQuantity * (unitPrice ?? 0);
  }

  /** True when the line carries a status that blocks purchase, so no price is shown for it. */
  isUnavailable(item: any): boolean {
    return !!item?.productData && DISABLED_STATUSES.has(item.productData.Status);
  }

  removeItem(id: string): void {
    const index = this.cartItems().findIndex((i) => i.id === id);
    if (index === -1) return;

    const itemEl = this.cartItemsRef.nativeElement.querySelectorAll('.summary-item')[index];
    // The row is not overflow:hidden (that would clip the quantity badge), so the
    // image is scaled down alongside the height collapse to keep it from spilling.
    gsap.to(itemEl, {
      opacity: 0,
      x: 20,
      height: 0,
      scale: 0.8,
      paddingTop: 0,
      paddingBottom: 0,
      marginBottom: 0,
      duration: 0.28,
      ease: 'power2.in',
      onComplete: () => {
        this.cartService.remove(id);
      },
    });
  }

  private _animateBadge(): void {
    const badge = this.cartBtnRef.nativeElement.querySelector('.cart-badge');
    if (!badge) return;
    gsap.fromTo(badge, { scale: 1.5 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1, 0.4)' });
  }

  ngOnDestroy(): void {
    this._openTl?.kill();
    this._closeTl?.kill();
  }

  proceedToCheckout(): void {
    if (this.isDisabled()) return;
    this.checkoutService.checkOutToCart(this.cartItems());
    this.router.navigate(['/payment/checkout']);
    this.closeCart();
  }
}
